/** Common GitHub permalink helpers for MergeOps artefacts. */

const REPO_OWNER = 'knowledge-work';
const REPO_NAME = 'knowledgework';

/** GitHub PR URL for a PR number in the deploy repo. */
export function pullRequestUrl(prNumber: string | number): string {
  return `https://github.com/${REPO_OWNER}/${REPO_NAME}/pull/${prNumber}`;
}

export function manifestYamlUrl(projectName: string, env: string): string {
  return (
    `https://github.com/${REPO_OWNER}/${REPO_NAME}/blob/master/manifests/` +
    `${encodeURIComponent(projectName)}/${encodeURIComponent(
      env,
    )}/manifest.yaml`
  );
}

/** GitHub Release for a MergeOps tag, e.g. recording-backend-set/20260525-1530. */
export function releaseUrl(projectName: string, tag: string): string {
  const t = `${projectName}/${tag}`;
  return `https://github.com/${REPO_OWNER}/${REPO_NAME}/releases/tag/${encodeURIComponent(
    t,
  )}`;
}

/**
 * Release branch for a deployed tag, e.g. release/auth-frontend/20251114-1425.
 * Mirrors the mergeops branch/release/compose action (`release/<project>/<tag>`),
 * and is the natural cherry-pick target for a given environment.
 */
export function releaseBranch(projectName: string, tag: string): string {
  return `release/${projectName}/${tag}`;
}

// Cloud Run deploy region per env (from terraform/core/<env>): dev runs in
// us-west1, stg/prd in asia-northeast1. The GCP project is knowledgework-<env>.
const CLOUD_RUN_REGION: Record<'dev' | 'stg' | 'prd', string> = {
  dev: 'us-west1',
  stg: 'asia-northeast1',
  prd: 'asia-northeast1',
};

/**
 * Cloud Run service detail page in the GCP console for a service's env. The
 * services use internal + load-balancer ingress (no public run.app URL), so the
 * console is the way to reach the service, its URL, revisions, logs and metrics.
 * Service name == project name for single-service projects.
 */
/**
 * GitHub Actions runs page for a workflow file, newest run first. After a
 * workflow_dispatch the just-triggered run shows at the top, so users can watch
 * progress and reach the PR it creates (or the failure).
 */
export function workflowRunsUrl(workflowFile: string): string {
  return `https://github.com/${REPO_OWNER}/${REPO_NAME}/actions/workflows/${workflowFile}`;
}

export function cloudRunUrl(
  projectName: string,
  env: 'dev' | 'stg' | 'prd',
): string {
  const region = CLOUD_RUN_REGION[env];
  return (
    `https://console.cloud.google.com/run/detail/${region}/` +
    `${encodeURIComponent(projectName)}/metrics?project=knowledgework-${env}`
  );
}

// MergeOps images are built by run_kit Cloud Build triggers in this project,
// one trigger per nx project named `build-<project>` (full name; run_kit only
// truncates the service-account account_id to 30 chars, never the trigger name).
const CLOUD_BUILD_PROJECT = 'knowledgework-build';
const CLOUD_BUILD_REGION = 'asia-northeast1';

/**
 * Cloud Build console builds list, pre-filtered to one trigger name. The
 * console stores filters in a `pageState` blob (not a simple query param), so
 * we substitute the trigger name into a verified-working template. Trigger
 * names are [a-z0-9-] so they need no extra encoding inside the blob.
 */
function buildsFilteredByTrigger(
  project: string,
  region: string,
  trigger: string,
): string {
  return (
    `https://console.cloud.google.com/cloud-build/builds;region=${region}` +
    `?project=${project}` +
    '&pageState=(%22builds%22:(%22f%22:%22%255B%257B_22k_22_3A_22' +
    // chip label ("トリガーの名前"); cosmetic, the `i:triggerName` field drives the filter.
    '%E3%83%88%E3%83%AA%E3%82%AC%E3%83%BC%E3%81%AE%E5%90%8D%E5%89%8D' +
    `_22_2C_22t_22_3A10_2C_22v_22_3A_22_5C_22${trigger}` +
    '_5C_22_22_2C_22s_22_3Atrue_2C_22i_22_3A_22triggerName_22%257D%255D%22))'
  );
}

/** Cloud Build console builds list, pre-filtered to a service's build trigger. */
export function cloudBuildUrl(projectName: string): string {
  return buildsFilteredByTrigger(
    CLOUD_BUILD_PROJECT,
    CLOUD_BUILD_REGION,
    `build-${projectName}`,
  );
}

/**
 * Cloud Build console builds list for a service's deploy_kit deploy, pre-filtered
 * to its trigger. deploy_kit triggers run in each env project (knowledgework-<env>)
 * in the `global` region, one per nx project named `deploy-<project>` (full name;
 * deploy_kit only truncates the service-account account_id to 30 chars, never
 * the trigger name). Used as the panel fallback before a build's own logUrl is
 * known.
 */
export function deployBuildUrl(
  projectName: string,
  env: 'stg' | 'prd',
): string {
  return buildsFilteredByTrigger(
    `knowledgework-${env}`,
    'global',
    `deploy-${projectName}`,
  );
}

/**
 * The deploy-panel equivalent for a Terraform component: tfez runs the apply in
 * the same env project and `global` region as deploy_kit, but names its trigger
 * `<project>-apply` (there is no `deploy-<project>` for terraform, so the
 * deploy_kit URL showed an empty list).
 */
export function terraformApplyBuildUrl(
  projectName: string,
  env: 'stg' | 'prd',
): string {
  return buildsFilteredByTrigger(
    `knowledgework-${env}`,
    'global',
    `${projectName}-apply`,
  );
}

const CLOUD_DEPLOY_REGION: Record<'stg' | 'prd', string> = {
  stg: 'asia-northeast1',
  prd: 'asia-northeast1',
};

/**
 * Cloud Deploy delivery pipelines list in the destination project. The precise
 * release/rollout id is only known after the deploy PR is merged, so rows link
 * to the environment's Cloud Deploy entry point.
 */
export function cloudDeployUrl(env: 'stg' | 'prd'): string {
  return (
    `https://console.cloud.google.com/deploy/delivery-pipelines` +
    `?project=knowledgework-${env}&region=${CLOUD_DEPLOY_REGION[env]}`
  );
}
